<template>
    <div :class="props.class">
        <ConfirmationModal v-if="askConfirmation" :title="confirmationTitle" :text="confirmationText" @emitResponse="checkConfirmationResponse" />
        <LoadingDialog v-if="loadingAction" />
        <Dialog 
            v-model:visible="saveLessonDialog" 
            modal 
            :header="selectedLesson.id == null ? 'Criar lição' : 'Editar lição'" 
            :draggable="false" 
            :style="{ width: '50rem' }"
        >
            <div class="flex items-center gap-4 mb-4">
                <label for="question" class="font-semibold w-24">Pergunta <RequiredMarker :isValid="dataToValidate.question" /></label>
                <InputText 
                    v-model="selectedLesson.question" 
                    id="question" 
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500" 
                />
            </div>
            <div class="flex items-center gap-4 mb-8">
                <label for="description" class="font-semibold w-24">Descrição</label>
                <InputText 
                    v-model="selectedLesson.description" 
                    id="description" 
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500" 
                />
            </div>
            <small>A lição é sempre uma pergunta com multiplas escolhas</small>
            <div class="flex justify-end gap-2">
                <Button label="Cancel" severity="secondary" @click="saveLessonDialog = false"></Button>
                <Button class="no-bg-button" label="Save" @click.prevent="saveLesson()"></Button>
            </div>
        </Dialog>
        <div class="px-2" v-if="loadingLessons">
            <LoaderComponent />
        </div>
        <p class="font-semibold text-2xl mb-10">
            Lições do vídeo: <span class="text-passion-fruit underline">{{ getTitleOfVideo() }}</span>
        </p>
        <div class="w-full text-left mb-5" v-if="modulesData.length == 0">
            <Button :label="'Nova lição'" class="no-bg-button" @click.prevent="openSaveModule(null)" />
        </div>
        <div v-if="!loadingLessons" class="draggable-list">
            <template v-for="(moduleData, index) of modulesData" :key="index">
                <div 
                    class="p-2 my-2 border border-iron rounded flex justify-between bg-passion-fruit text-white font-semibold flex-wrap flex-col md:flex-nowrap md:flex-row" 
                >
                    <div class="w-full md:w-auto">
                        {{ moduleData.question }}
                    </div> 
                    <div class="flex flex-wrap justify-center md:justify-between w-full md:w-auto md:flex-row mt-5 mb:mt-0">
                        <Button
                            class="no-bg-button"
                            :icon="'pi pi-pen-to-square'"
                            @click="openSaveModule(moduleData.id)"
                            v-tooltip.top="'Editar lição'"
                        />
                        <Button
                            class="no-bg-button mx-3"
                            :icon="'pi pi-list-check'"
                            v-tooltip.top="'Gerenciar respostas'"
                            @click.prevent="openLessionAnwserSection(moduleData.id)" 
                        />
                        <Button 
                            class="no-bg-button"
                            icon="pi pi-trash" 
                            v-tooltip.top="'Remover lição'" 
                            @click.prevent="openConfirmationModal('removeVideoLesson', moduleData.id)" 
                        />
                    </div>
                </div>
            </template>
        </div>

        <ModuleVideosLessonsAnswersComponent :lessonId="lessonId" @closed="lessonId = null" />
    </div>
</template>
<script setup>
    import { defineProps, onMounted, reactive, ref, watch } from 'vue';
    import { useToast } from 'primevue/usetoast';
    import { showToast } from '@/composables/toast';
    import utils from '@/composables/utils';
    import LoaderComponent from '@/components/Utils/LoaderComponent.vue';
    import Button from 'primevue/button';
    import Dialog from 'primevue/dialog';
    import InputText from 'primevue/inputtext';
    import RequiredMarker from '@/components/misc/RequiredMarkerComponent.vue';
    import { isTextValid } from '@/composables/textUtils';
    import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
    import ConfirmationModal from '@/components/Utils/ConfirmationModal.vue';
    import ModuleVideosLessonsAnswersComponent from './ModuleVideosLessonsAnswersComponent.vue';
    import CourseServiceLessonService from '@/services/CourseVideoLessonService';

    const toast = useToast();
    const props = defineProps({
        videoData: {
            type: Object,
            required: true
        },
        courseUuid: {
            type: Object,
            required: true
        },
        class: {
            type: String,
            required: false,
            default: ''
        }
    });

    let selectedLesson = ref({
        id: null,
        question: null,
        description: null,
        multipleChoices: true
    });
    let loadingAction = ref(false);
    let saveLessonDialog = ref(false);
    let loadingLessons = ref(true);
    let modulesData = ref([]);
    let moduleById = ref({});
    let courseUuid = ref(null);

    let videoData = ref(null);
    onMounted(() => {
        videoData.value = utils.copyVariable(props.videoData);
        courseUuid.value = props.courseUuid;
        getLessons();
    });

    watch(() => props.videoData, (newValue) => {
        videoData.value = utils.copyVariable(newValue);
        getLessons();
    },
        { deep: true }
    );

    let lessonId = ref(null);
    function openLessionAnwserSection(id = null){
        lessonId.value = id;
    }

    async function getLessons(){
        loadingLessons.value = true;

        let response = await CourseServiceLessonService.getLessonsByVideo(videoData.value.id);
        const modules = response.getContent();

        loadingLessons.value = false;
        moduleById.value = {};
        modulesData.value = [];

        for(let mod of modules){
            mod.multipleChoices = true;
            modulesData.value.push(mod);
            moduleById.value[mod.id] = mod;
        }
    }

    async function openSaveModule(id = ''){
        saveLessonDialog.value = true;
        if(moduleById.value[id] == undefined){
            resetselectedLesson();
            return;
        }
        selectedLesson.value = utils.copyVariable(moduleById.value[id]);
    }

    let dataToValidate = reactive({
        question: true
    })
    let errorMessages = {
        question: 'Por favor informe a pergunta'
    }
    async function saveLesson(){
        let isValid = true;
        for(let attr of Object.keys(dataToValidate)){
            if(!isTextValid(selectedLesson.value[attr])){
                isValid = false;
                dataToValidate[attr] = false;
                showToast(toast, errorMessages[attr], 'error');
                break;
            }else{
                dataToValidate[attr] = true;
            }
        }
        if(!isValid){
            return;
        }
        let lessonData = utils.copyVariable(selectedLesson.value);
        lessonData.course_module_video = videoData.value.id;
        loadingAction.value = true;

        let response = await CourseServiceLessonService.saveLesson(lessonData, lessonData.id);
        
        loadingAction.value = false;
        if(!response.success()){
            showToast(toast, response.getMessage(), 'error');
            return;
        }
        showToast(toast, lessonData.id != null ? 'Lição atualizado' : 'Lição criado');
        saveLessonDialog.value = false;
        getLessons(true);
    }

    function resetselectedLesson(){
        selectedLesson.value = {
            id: null,
            question: null,
            description: null,
            multipleChoices: false
        }
    }

    let askConfirmation = ref(false);
    let selectedConfirmation = ref('');
    let confirmationMethodVariable = ref('');
    let confirmationTitle = ref('');
    let confirmationText = ref('');
    let confirmationModalValues = ref({
        removeVideoLesson: {
            title: 'Are you sure you wish to remove this lesson?',
            text: 'this action can not be undone and all the answers will be removed too'
        }
    })
    function openConfirmationModal(action = 'removeVideoLesson', identifier = ''){
        confirmationTitle.value = confirmationModalValues.value[action].title;
        confirmationText.value = confirmationModalValues.value[action].text;
        selectedConfirmation.value = action;
        confirmationMethodVariable.value = identifier; 
        askConfirmation.value = true;
    }
    function checkConfirmationResponse(value){
        if([null,undefined].includes(value))
            return;
        const confirmation = value.confirmation;
        askConfirmation.value = false;
        if(confirmation){
            switch(selectedConfirmation.value){
                case 'removeVideoLesson':
                    removeVideoLesson(confirmationMethodVariable.value);
                break;
            }
        }
    }

    async function removeVideoLesson(id = ''){
        loadingAction.value = true;

        const response = await CourseServiceLessonService.removeVideoLesson(id);

        loadingAction.value = false;
        if(!response.success()){
            showToast(toast, response.getMessage(), 'error');
            return;
        }
        showToast(toast, response.getMessage());
        getLessons(true);
    }

    function getTitleOfVideo(){
        if(videoData.value == null)
            return '';
        return videoData.value.description;
    }
</script>